import {
  parFloat,
  tcGecerliMi,
  vergiGecerliMi,
  parseTarih,
  enumNormalize,
} from '@/classes/topluTakip/excelDogrula';

/**
 * Excel Aktar sütun şeması + satır validatörü (DEV-2784, Faz 3b).
 *
 * Şablon sözleşmesindeki 38 standart sütunun tip + geçerli değer tanımı burada tek
 * kaynaktan gelir. satiriDogrula bir satırı bu şemaya ve koşullu zorunluluklara göre
 * doğrular, hatalı hücreleri (alan + neden + mesaj) döndürür.
 */

export type SutunTip = 'metin' | 'sayi' | 'rakam' | 'tarih' | 'tc_vergi' | 'enum';

export interface SutunKural {
  baslik: string;
  tip: SutunTip;
  enumDegerler?: string[];
}

export type HataNeden = 'bos' | 'format' | 'gecersiz_deger';

export interface HucreHatasi {
  alan: string;
  baslik: string;
  neden: HataNeden;
  mesaj: string;
}

const TAKIP_MAHIYETLERI = [
  'BELGESİZ', 'ÇOCUK TESLİMİ', 'DİĞER', 'DOĞALGAZ', 'ELEKTRİK', 'İNTERNET/TV',
  'KREDİ KARTI', 'KREDİ SÖZLEŞMESİ', 'NAFAKA', 'SU', 'TELEFON (CEP)',
  'TELEFON (SABİT)', 'TÜKETİCİ HAKEM HEYETİ',
];

/** Şablon sözleşmesi — sütun → tip/geçerli değer. metin sütunlarında yalnız boş kontrolü yapılır. */
export const SUTUN_KURALLARI: Record<string, SutunKural> = {
  tekil_alan: { baslik: 'TEKİL ALAN', tip: 'sayi' },
  takip_tipi: { baslik: 'TAKİP TİPİ', tip: 'sayi' },
  takip_yolu: { baslik: 'TAKİP YOLU', tip: 'enum', enumDegerler: ['HACİZ', 'İHTİYATİ HACİZ - HACİZ', 'HACİZ - SATIŞ', 'DİĞER'] },
  takip_tarihi: { baslik: 'TAKİP TARİHİ', tip: 'tarih' },
  takip_mahiyeti: { baslik: 'TAKİP MAHİYETİ', tip: 'enum', enumDegerler: TAKIP_MAHIYETLERI },
  icra_dairesi: { baslik: 'İCRA DAİRESİ', tip: 'metin' },
  esas_no: { baslik: 'ESAS NO', tip: 'metin' },
  tuketici_kodu: { baslik: 'TÜKETİCİ KODU', tip: 'metin' },
  alacakli_kurum_mu: { baslik: 'ALACAKLI KURUM/KİŞİ', tip: 'enum', enumDegerler: ['KURUM', 'KİŞİ'] },
  alacakli_ad: { baslik: 'ALACAKLI AD SOYAD', tip: 'metin' },
  alacakli_isim: { baslik: 'ALACAKLI KURUM İSİM', tip: 'metin' },
  alacakli_tc_vergi: { baslik: 'ALACAKLI VERGİ/TC NO', tip: 'tc_vergi' },
  borclu_ad: { baslik: 'BORÇLU AD SOYAD', tip: 'metin' },
  borclu_isim: { baslik: 'BORÇLU KURUM İSİM', tip: 'metin' },
  // TDT-3360 test dönüşü: borçlu TC/vergi no boş veya farklı uzunlukta olabilir;
  // kullanıcının girdiği değer aktarımı durdurmamalıdır. Alacaklı kimlik kontrolü korunur.
  borclu_tc_vergi: { baslik: 'BORÇLU VERGİ/TC NO', tip: 'rakam' },
  borclu_cinsiyet: { baslik: 'BORÇLU CİNSİYET', tip: 'metin' },
  borclu_adres_bilgisi: { baslik: 'BORÇLU ADRESİ', tip: 'metin' },
  borclu_il: { baslik: 'BORÇLU İL', tip: 'metin' },
  borclu_ilce: { baslik: 'BORÇLU İLÇE', tip: 'metin' },
  borclu_kurum_mu: { baslik: 'BORÇLU TÜRÜ ŞAHIS ŞİRKET', tip: 'enum', enumDegerler: ['KURUM', 'KİŞİ'] },
  cepTelefon: { baslik: 'BORÇLU CEP TELEFON', tip: 'sayi' },
  tutar: { baslik: 'BELGE TUTARI', tip: 'sayi' },
  vade_tarihi: { baslik: 'VADE TARİHİ', tip: 'tarih' },
  faiz_baslangic_tarihi: { baslik: 'FAİZ BAŞLANGIÇ TARİHİ', tip: 'tarih' },
  alacak_aciklama: { baslik: 'ALACAK AÇIKLAMASI', tip: 'metin' },
  // Faiz türleri Excel'de sıra numarasıyla gelir, parse'ta tenant faiz İSMİNE çevrilir
  // (backend faizi isimle çözüyor) → tip 'metin'.
  takip_oncesi_faiz_turu: { baslik: 'TAKİP ÖNCESİ FAİZ TÜRÜ', tip: 'metin' },
  takip_sonrasi_faiz_turu: { baslik: 'TAKİP SONRASI FAİZ TÜRÜ', tip: 'metin' },
  elle_faiz_mi: { baslik: 'ELLE FAİZ Mİ', tip: 'enum', enumDegerler: ['EVET', 'HAYIR', 'Yasal Faiz'] },
  elle_faiz_tutar_mi: { baslik: 'ORAN/TUTAR', tip: 'enum', enumDegerler: ['ORAN', 'TUTAR'] },
  yillik_mi: { baslik: 'AYLIK/YILLIK', tip: 'enum', enumDegerler: ['AYLIK', 'YILLIK'] },
  elle_faiz_tutari: { baslik: 'İŞLEYEN FAİZ', tip: 'sayi' },
  elle_faiz_oran: { baslik: 'ORAN', tip: 'sayi' },
};

const FAIZ_ALT_ALANLAR = ['elle_faiz_tutar_mi', 'yillik_mi', 'elle_faiz_tutari', 'elle_faiz_oran'];

/** takip_talepleri.manuel_takip_yolu kolonu varchar(50) — UI ve doğrulama bu sınıra uyar. */
export const MANUEL_TAKIP_YOLU_MAX = 50;

/**
 * Bir alanın bu satır bağlamında zorunlu olup olmadığı.
 * Mevcut eksikAlanlar koşullarını korur; eski koddaki "EVET+ORAN diğer alanları
 * atlıyor" kusuru YOKTUR (her koşul bağımsız değerlendirilir).
 */
export function alanZorunluMu(alan: string, item: any): boolean {
  // TDT-3360 test dönüşünde açıkça isteğe bağlı bırakılan alanlar. İl/ilçe/adres boşsa
  // kullanıcı aktarımı tamamlayabilir; eksik adresi daha sonra takipte düzenleyebilir.
  const herZamanOpsiyonel = [
    'cepTelefon',
    'tuketici_kodu',
    'borclu_tc_vergi',
    'alacakli_tc_vergi',
    'borclu_adres_bilgisi',
    'borclu_il',
    'borclu_ilce',
    // TDT-3470: takip henüz açılmamış dosyada icra dairesi ve esas no daha oluşmamıştır;
    // aktarımı durdurmamalıdır. Backend bu ikisini zaten boş kabul eder (takip
    // 'acilmamis' durumuyla oluşur), kullanıcı esas noyu takip açılınca girer.
    'icra_dairesi',
    'esas_no',
  ];
  if (herZamanOpsiyonel.includes(alan)) {
    return false;
  }

  const borcluKurum = enumNormalize(item.borclu_kurum_mu, ['KURUM', 'KİŞİ']);
  const alacakliKurum = enumNormalize(item.alacakli_kurum_mu, ['KURUM', 'KİŞİ']);
  const elleFaiz = enumNormalize(item.elle_faiz_mi, ['EVET', 'HAYIR', 'Yasal Faiz']);
  const oranTutar = enumNormalize(item.elle_faiz_tutar_mi, ['ORAN', 'TUTAR']);

  // Borçlu türü: KURUM → kişi alanları gerekmez; KİŞİ → kurum ismi gerekmez.
  if (borcluKurum === 'KURUM' && ['borclu_ad', 'borclu_soyad', 'borclu_cinsiyet'].includes(alan)) return false;
  if (borcluKurum === 'KİŞİ' && alan === 'borclu_isim') return false;
  // Alacaklı türü.
  if (alacakliKurum === 'KURUM' && ['alacakli_ad', 'alacakli_soyad'].includes(alan)) return false;
  if (alacakliKurum === 'KİŞİ' && alan === 'alacakli_isim') return false;
  // Faiz alt alanları: yalnız elle faiz EVET iken devreye girer.
  if (FAIZ_ALT_ALANLAR.includes(alan)) {
    if (elleFaiz !== 'EVET') return false;
    if (alan === 'elle_faiz_oran') return oranTutar === 'ORAN';
    if (alan === 'elle_faiz_tutari') return oranTutar === 'TUTAR';
    return true; // elle_faiz_tutar_mi, yillik_mi → EVET ise zorunlu
  }

  return true;
}

/** Dolu bir değeri tipine göre doğrular; geçersizse hata döndürür. */
function tipHatasi(alan: string, kural: SutunKural, deger: any): HucreHatasi | null {
  switch (kural.tip) {
    case 'tarih':
      if (parseTarih(deger) === null) {
        return { alan, baslik: kural.baslik, neden: 'format', mesaj: `${kural.baslik}: geçerli bir tarih değil (GG/AA/YYYY bekleniyor).` };
      }
      break;
    case 'sayi':
      if (parFloat(deger) === null) {
        return { alan, baslik: kural.baslik, neden: 'format', mesaj: `${kural.baslik}: geçerli bir sayı değil.` };
      }
      break;
    case 'rakam':
      // Hane sayısı bilinçli olarak serbesttir; yalnız sessiz dönüşüm/veri kaybını
      // önlemek için harf ve ayraç kabul edilmez.
      if (!/^\d+$/.test(String(deger).trim())) {
        return { alan, baslik: kural.baslik, neden: 'format', mesaj: `${kural.baslik}: yalnız rakamlardan oluşmalıdır.` };
      }
      break;
    case 'tc_vergi':
      if (!tcGecerliMi(deger) && !vergiGecerliMi(deger)) {
        return { alan, baslik: kural.baslik, neden: 'gecersiz_deger', mesaj: `${kural.baslik}: geçerli bir TC (11 hane) veya vergi no (10 hane) değil.` };
      }
      break;
    case 'enum':
      if (enumNormalize(deger, kural.enumDegerler || []) === null) {
        return { alan, baslik: kural.baslik, neden: 'gecersiz_deger', mesaj: `${kural.baslik}: geçersiz değer. Beklenen: ${(kural.enumDegerler || []).join(', ')}.` };
      }
      break;
    // metin → format kontrolü yok
  }
  return null;
}

/**
 * Enum sütunlarını yerinde kanonik forma normalize eder ("evet"→"EVET", "haciz"→"HACİZ").
 * Backend tam eşleşme beklediği için parse sonrası çağrılır. Kanonik forma eşlenemeyen
 * değere dokunulmaz — satiriDogrula onu "geçersiz değer" olarak işaretler.
 */
export function enumAlanlariNormalizeEt(item: any): void {
  if (!item) return;
  for (const alan of Object.keys(SUTUN_KURALLARI)) {
    const kural = SUTUN_KURALLARI[alan];
    if (kural.tip !== 'enum') continue;
    const deger = item[alan];
    if (deger === undefined || deger === null || String(deger).trim() === '') continue;
    const kanonik = enumNormalize(deger, kural.enumDegerler || []);
    if (kanonik !== null) {
      item[alan] = kanonik;
    }
  }
}

/** Bir satırı şema + koşullu zorunluluklara göre doğrular; hatalı hücreleri döndürür. */
export function satiriDogrula(item: any, sutunlar: Record<string, SutunKural> = SUTUN_KURALLARI): HucreHatasi[] {
  const hatalar: HucreHatasi[] = [];

  for (const alan of Object.keys(sutunlar)) {
    const kural = sutunlar[alan];
    const deger = item ? item[alan] : undefined;
    const bos = deger === undefined || deger === null || String(deger).trim() === '';

    if (bos) {
      if (alanZorunluMu(alan, item)) {
        hatalar.push({ alan, baslik: kural.baslik, neden: 'bos', mesaj: `${kural.baslik}: boş bırakılamaz.` });
      }
      continue; // boşsa tip/format kontrolü yapılmaz
    }

    const hata = tipHatasi(alan, kural, deger);
    if (hata) hatalar.push(hata);
  }

  // Özel kural: takip yolu "DİĞER" seçildiğinde serbest metin (manuel_takip_yolu) zorunludur.
  const yol = enumNormalize(item ? item.takip_yolu : null, SUTUN_KURALLARI.takip_yolu.enumDegerler || []);
  if (yol === 'DİĞER') {
    const mtd = item ? item.manuel_takip_yolu : undefined;
    if (mtd === undefined || mtd === null || String(mtd).trim() === '') {
      hatalar.push({
        alan: 'manuel_takip_yolu',
        baslik: 'TAKİP YOLU (DİĞER)',
        neden: 'bos',
        mesaj: 'Takip yolu "Diğer" seçildiğinde açıklama girilmelidir.',
      });
    } else if (String(mtd).trim().length > MANUEL_TAKIP_YOLU_MAX) {
      // Veritabanı kolonu varchar(50): aşarsa aktarım sırasında hata/kırpılma olur; burada yakala.
      hatalar.push({
        alan: 'manuel_takip_yolu',
        baslik: 'TAKİP YOLU (DİĞER)',
        neden: 'format',
        mesaj: `Takip yolu açıklaması en fazla ${MANUEL_TAKIP_YOLU_MAX} karakter olabilir.`,
      });
    }
  }

  return hatalar;
}
